/**
 * Client-side sanity checks for an SSH public key, and how to name one.
 *
 * The server validates authoritatively (`PUT /api/v1/me`, see `docs/API.md`);
 * this only catches the two mistakes that are worth catching before a round
 * trip: pasting a private key, and pasting something that is not a key at all.
 */

import { sha256 } from "./sha256";

const PREFIXES = [
  "ssh-ed25519",
  "ssh-rsa",
  "ecdsa-sha2-nistp256",
  "ecdsa-sha2-nistp384",
  "ecdsa-sha2-nistp521",
];

/** How an account holding several keys tells them apart. */
export interface SshKeyIdentity {
  /** `ssh-ed25519`, `ssh-rsa`, … — or `unknown` when the line is malformed. */
  algorithm: string;
  /**
   * `SHA256:…`, exactly what `ssh-keygen -lf` prints for the same key.
   *
   * Empty when the key material cannot be decoded, which the server would have
   * refused: the row then falls back to the algorithm and the comment.
   */
  fingerprint: string;
  /**
   * The trailing comment, usually `user@machine`.
   *
   * This is what actually names the machine a key belongs to, so it is the
   * part a researcher recognises. Empty when the key carries none.
   */
  comment: string;
}

/**
 * Decode a base64 blob into bytes.
 *
 * Args:
 *     blob: The base64 text.
 *
 * Returns:
 *     The bytes, or null when the text is not valid base64.
 */
function decodeBase64(blob: string): Uint8Array | null {
  try {
    const binary = atob(blob);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }
    return bytes;
  } catch {
    return null;
  }
}

/**
 * Read what identifies a stored public key, without reprinting all of it.
 *
 * An account holds one key per machine, so "which key is this?" is the
 * question every row has to answer: the comment names the machine, and the
 * fingerprint is what the researcher can compare with `ssh-keygen -lf` on that
 * machine when the comment is missing or lies.
 *
 * Args:
 *     key: The stored public key, one line.
 *
 * Returns:
 *     Its algorithm, its `SHA256:…` fingerprint and its comment.
 */
export function readSshPublicKey(key: string): SshKeyIdentity {
  const [algorithm, blob, ...comment] = key.trim().split(/\s+/);
  const bytes = blob ? decodeBase64(blob) : null;
  return {
    algorithm: algorithm ?? "unknown",
    fingerprint: bytes
      ? `SHA256:${btoa(String.fromCharCode(...sha256(bytes))).replace(/=+$/, "")}`
      : "",
    comment: comment.join(" "),
  };
}

/**
 * Describe a stored public key in one line.
 *
 * Args:
 *     key: The stored public key.
 *
 * Returns:
 *     One line, e.g. `ssh-ed25519 SHA256:JcP/… · alice@laptop`.
 */
export function describeSshPublicKey(key: string): string {
  const { algorithm, fingerprint, comment } = readSshPublicKey(key);
  return [algorithm, fingerprint, comment].filter(Boolean).join(" · ");
}

/**
 * Check an SSH public key before sending it.
 *
 * Args:
 *     value: What the researcher pasted.
 *
 * Returns:
 *     An actionable error message, or null when the key looks acceptable.
 */
export function validateSshPublicKey(value: string): string | null {
  const key = value.trim();
  if (!key) {
    return "Paste your public key first.";
  }
  if (key.includes("PRIVATE KEY")) {
    return (
      "That is your private key — keep it. The console needs the public one, " +
      "the short file ending in .pub."
    );
  }
  if (key.split(/\r?\n/).length > 1) {
    return "A public key is a single line. Remove the line breaks.";
  }
  const [algorithm, blob] = key.split(/\s+/);
  if (!algorithm || !PREFIXES.includes(algorithm)) {
    return `A public key starts with ${PREFIXES.slice(0, 2).join(" or ")}. This one starts with "${algorithm ?? ""}".`;
  }
  if (!blob || !/^[A-Za-z0-9+/]+=*$/.test(blob) || blob.length < 32) {
    return "The key itself looks truncated. Copy the whole line of the .pub file.";
  }
  return null;
}
